import { useState } from "react";
import { Hint } from "@/ui/Hint";
import {
  Button,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { LuSearch, LuX } from "react-icons/lu";

import { Table } from "@/ui/tables/Table";
import type { TableProps } from "@/ui/tables/Table";
import { ModalSearchProduct } from "@/core/products/components/ModalSearchProduct";

import type { ProductState } from "@/core/products/components/ProductsTable";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  CreateProductKitInput,
  createProductKitSchema,
} from "../schemas/productKitSchema";
import { useUpdateKit } from "@/core/kits/useCases/useUpdateKit";
import { Kit } from "@/shared/core-product-kits/types/Kit";
import { useGetStorage } from "@/core/storages/useCases/useGetStorage";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { NativeSelectField, NativeSelectRoot } from "@/ui/chakra/native-select";
import { toast } from "@/ui/chakra/toaster";

interface EditModalProductKitProps {
  isOpen: boolean;
  onClose: () => void;
  selectedKit: Kit;
}

const EditModalProductKit = ({
  isOpen,
  onClose,
  selectedKit,
}: EditModalProductKitProps) => {
  const { session } = useAuth();
  const [isErpModalOpen, setErpModalOpen] = useState(false);

  const { mutate: updateKit, isPending } = useUpdateKit();

  const { data: storages } = useGetStorage(session?.zone ?? "");

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm<CreateProductKitInput>({
    resolver: zodResolver(createProductKitSchema),
    defaultValues: {
      name: selectedKit?.name || "",
      enabled: selectedKit?.enabled ?? true,
      storageId: selectedKit?.storageId ?? "",
      products: selectedKit?.products
        ? selectedKit.products.map((product) => ({
            data: {
              id: product.id,
              stock: product.stock,
              description: product.description,
              unit: product.unit ?? "",
              location: product.location ?? "",
              minimumStock: product.minimumStock ?? 0,
              storageId: product.storageId ?? "",
              storage: product.storage ?? { id: "", name: "" },
              converter: product.converter ?? 0,
              converterTypeId: product.converterTypeId ?? "",
              converterType: product.converterType ?? {
                id: "",
                name: "",
              },
              enabled: product.enabled ?? true,
            },
            amount: product.kitProduct.quantity,
          }))
        : [],
    },
  });

  const { products, storageId } = watch();

  const handleAddProduct = (product: ProductState) => {
    if (products.some((p) => p.data.id === product.data.id)) return;

    setValue("products", [...products, product]);
  };

  const handleRemoveProduct = (productId: string) => {
    if (products.length <= 1) {
      toast({
        title: "Acción no permitida",
        description: "El kit debe tener al menos un producto.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setValue(
      "products",
      products.filter((product) => product.data.id !== productId),
    );
  };

  const onFormSubmit = (data: CreateProductKitInput) => {
    if (data.products.length === 0) {
      toast({
        title: "Error al guardar cambios",
        description: "El kit debe contener al menos un producto.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formattedProducts = data.products.map((product) => ({
      id: product.data.id,
      quantity: product.amount,
      price: product.price || 0,
    }));

    updateKit(
      {
        id: selectedKit.id,
        data: {
          ...data,
          products: formattedProducts,
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "Kit de productos actualizado",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
        },
        onError: () => {
          toast({
            title: "Error al actualizar el kit",
            description:
              "Ocurrió un error al intentar actualizar el kit. Por favor, inténtalo de nuevo.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        },
      },
    );
  };

  const getSelectedProductsColumns =
    (): TableProps<ProductState>["headers"] => [
      {
        label: "Producto",
        itemLabel: (item) => item.data.description,
      },
      {
        label: "Almacén",
        itemLabel: (item) => item.data.storage.name,
      },
      {
        label: "Cantidad",
        align: "end",
        itemLabel: (item) => (
          <Input
            size="sm"
            type="number"
            w="90px"
            textAlign="end"
            aria-label="Cantidad"
            value={item.amount}
            onChange={(e) => {
              setValue(
                "products",
                products.map((p) =>
                  p.data.id === item.data.id
                    ? {
                        ...p,
                        amount: Math.max(0, Number(e.target.value)),
                      }
                    : p,
                ),
              );
            }}
          />
        ),
      },

      {
        label: "",
        align: "end",
        itemLabel: (item) => (
          <IconButton
            aria-label="Quitar producto"
            size="sm"
            variant="ghost"
            color="fg.muted"
            onClick={() => handleRemoveProduct(item.data.id)}
          >
            <LuX />
          </IconButton>
        ),
      },
    ];

  const handleOnClose = () => {
    onClose();
    reset();
  };

  const storageField = register("storageId");

  return (
    <>
      <DialogRoot isOpen={isOpen} onClose={handleOnClose} size="xl" isCentered>
        <DialogContent maxW="5xl">
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <DialogHeader borderBottomWidth="1px" borderColor="border">
              <DialogTitle>Editar kit de productos</DialogTitle>
            </DialogHeader>
            <DialogCloseTrigger />
            <DialogBody display="flex" flexDirection="column" gap={6} pt={5}>
              <Hint>
                Un kit agrupa productos con sus cantidades para agregarlos a un
                vale de una sola vez, desde el botón «Código kit».
              </Hint>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <Field
                  label="Nombre del kit"
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                >
                  <Input {...register("name")} placeholder="Nombre del kit" />
                </Field>
                <Field
                  label="Bodega"
                  required
                  invalid={!!errors.storageId}
                  errorText={errors.storageId?.message?.toString()}
                >
                  <NativeSelectRoot>
                    <NativeSelectField
                      placeholder="Seleccione una bodega"
                      {...storageField}
                      onChange={(e) => {
                        storageField.onChange(e);
                        setValue("products", []);
                      }}
                    >
                      {storages?.map((storage) => (
                        <option key={storage.id} value={storage.id}>
                          {storage.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </SimpleGrid>

              <Field
                invalid={!!errors.products}
                errorText={errors.products?.message}
              >
                <Flex
                  align="center"
                  justify="space-between"
                  gap={4}
                  wrap="wrap"
                  w="full"
                  mb={3}
                >
                  <Text fontWeight="semibold">
                    Productos{" "}
                    <Text as="span" color="fg.muted" fontWeight="normal">
                      · {products.length}
                    </Text>
                  </Text>
                  <Button
                    variant="outline"
                    fontWeight="normal"
                    color="fg.muted"
                    justifyContent="flex-start"
                    w={{ base: "full", sm: "320px" }}
                    onClick={() => setErpModalOpen(true)}
                    type="button"
                  >
                    <LuSearch />
                    Agregar producto...
                  </Button>
                </Flex>
                <Table
                  headers={getSelectedProductsColumns()}
                  data={products}
                  extractItemKey={(item) => item.data.id}
                  showPagination={false}
                  showDefaultActions={false}
                  showSearch={false}
                  showConfigColumns={false}
                />
              </Field>
            </DialogBody>
            <DialogFooter borderTopWidth="1px" borderColor="border">
              <Button variant="outline" onClick={handleOnClose} type="button">
                Cancelar
              </Button>
              <Button colorPalette="brand" type="submit" loading={isPending}>
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogRoot>

      <ModalSearchProduct
        isOpen={isErpModalOpen}
        onClose={() => setErpModalOpen(false)}
        selected={products}
        onChange={(product) => handleAddProduct(product)}
        storageId={storageId}
      />
    </>
  );
};

export default EditModalProductKit;
